import React, { ReactNode, useState } from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import { type CalendarDate, parseDate } from "@internationalized/date";
import {
  Button,
  DateField,
  IconButton,
  ItemsPerPage,
  Pagination,
  SingleSelectField,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
} from "@shoptet/ui";
import { DateTime } from "luxon";
import { Trans, useTranslation } from "react-i18next";
import {
  AdSystemT,
  CreditTransactionKindT,
  FeatureFlagT,
  useCreditTransactionsQuery,
} from "../../graphql/generated/graphql";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { formatTime } from "../../i18n/formatDate";
import { formatMoney } from "../../i18n/formatNumbers";
import { SmallLoader } from "../SmallLoader";

const asRichText = <T extends ReactNode>(node: T) => node as T & { __brand: "RichText" };

enum SystemFilter {
  ALL = "all",
  GOOGLE = "google",
  META = "meta",
}

enum TransactionTypeFilter {
  ALL = "all",
  CURRENCY_EXCHANGE_DECREASE = "CURRENCY_EXCHANGE_DECREASE",
  CURRENCY_EXCHANGE_INCREASE = "CURRENCY_EXCHANGE_INCREASE",
  DECREASE = "DECREASE",
  INCREASE = "INCREASE",
  OVERCHARGE = "OVERCHARGE",
  TARIF_EXCHANGE_DECREASE = "TARIF_EXCHANGE_DECREASE",
  TARIF_EXCHANGE_INCREASE = "TARIF_EXCHANGE_INCREASE",
  USAGE = "USAGE",
}

type ExtendedDateFieldProps = React.ComponentProps<typeof DateField> & {
  maxValue?: CalendarDate;
  minValue?: CalendarDate;
};

const ExtendedDateField = (props: ExtendedDateFieldProps) => <DateField {...props} />;

export const CreditTransactions = () => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.CreditTransactions" });
  const { t: tPagination } = useTranslation("translation", { keyPrefix: "Components.Pagination" });
  const { enabled: hasMetaCampaignsEnabled } = useFeatureFlag(FeatureFlagT.MetaCampaignsT);

  const savedItemsPerPage = parseInt(localStorage.getItem("shoptet_credit_transactions_items_per_page") ?? "10");
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<ItemsPerPage>(savedItemsPerPage as ItemsPerPage);

  // Applied filter states (used in query)
  const [appliedDateFrom, setAppliedDateFrom] = useState<string>("");
  const [appliedDateTo, setAppliedDateTo] = useState<string>(DateTime.local().toFormat("yyyy-MM-dd"));
  const [appliedSystemFilter, setAppliedSystemFilter] = useState<SystemFilter>(SystemFilter.ALL);
  const [appliedTransactionTypeFilter, setAppliedTransactionTypeFilter] = useState<TransactionTypeFilter>(
    TransactionTypeFilter.ALL
  );

  // Temporary filter states (for the form)
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>(DateTime.local().toFormat("yyyy-MM-dd"));
  const [systemFilter, setSystemFilter] = useState<SystemFilter>(SystemFilter.ALL);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<TransactionTypeFilter>(TransactionTypeFilter.ALL);

  const systemOptions = [
    { label: t("filter.system all"), value: SystemFilter.ALL },
    { label: t("filter.system google"), value: SystemFilter.GOOGLE },
    { label: t("filter.system meta"), value: SystemFilter.META },
  ];

  const transactionTypeOptions = [
    { label: t("transaction kind.all"), value: TransactionTypeFilter.ALL },
    { label: t("transaction kind.INCREASE"), value: TransactionTypeFilter.INCREASE },
    { label: t("transaction kind.DECREASE"), value: TransactionTypeFilter.DECREASE },
    { label: t("transaction kind.OVERCHARGE"), value: TransactionTypeFilter.OVERCHARGE },
    { label: t("transaction kind.USAGE"), value: TransactionTypeFilter.USAGE },
    {
      label: t("transaction kind.CURRENCY_EXCHANGE_INCREASE"),
      value: TransactionTypeFilter.CURRENCY_EXCHANGE_INCREASE,
    },
    {
      label: t("transaction kind.CURRENCY_EXCHANGE_DECREASE"),
      value: TransactionTypeFilter.CURRENCY_EXCHANGE_DECREASE,
    },
    { label: t("transaction kind.TARIF_EXCHANGE_INCREASE"), value: TransactionTypeFilter.TARIF_EXCHANGE_INCREASE },
    { label: t("transaction kind.TARIF_EXCHANGE_DECREASE"), value: TransactionTypeFilter.TARIF_EXCHANGE_DECREASE },
  ];

  const getAdSystemForQuery = (filter: SystemFilter): AdSystemT | undefined => {
    if (filter === SystemFilter.GOOGLE) {
      return AdSystemT.GoogleAdsT;
    }
    if (filter === SystemFilter.META) {
      return AdSystemT.MetaAdsT;
    }
    return undefined;
  };

  const getKindsForQuery = (filter: TransactionTypeFilter): CreditTransactionKindT[] | undefined => {
    if (filter === TransactionTypeFilter.ALL) {
      return undefined;
    }

    const filterToKindMap: Record<string, CreditTransactionKindT> = {
      [TransactionTypeFilter.INCREASE]: CreditTransactionKindT.IncreaseT,
      [TransactionTypeFilter.DECREASE]: CreditTransactionKindT.DecreaseT,
      [TransactionTypeFilter.OVERCHARGE]: CreditTransactionKindT.OverchargeT,
      [TransactionTypeFilter.USAGE]: CreditTransactionKindT.UsageT,
      [TransactionTypeFilter.CURRENCY_EXCHANGE_INCREASE]: CreditTransactionKindT.CurrencyExchangeIncreaseT,
      [TransactionTypeFilter.CURRENCY_EXCHANGE_DECREASE]: CreditTransactionKindT.CurrencyExchangeDecreaseT,
      [TransactionTypeFilter.TARIF_EXCHANGE_INCREASE]: CreditTransactionKindT.TarifExchangeIncreaseT,
      [TransactionTypeFilter.TARIF_EXCHANGE_DECREASE]: CreditTransactionKindT.TarifExchangeDecreaseT,
    };

    const kind = filterToKindMap[filter];
    return kind ? [kind] : undefined;
  };

  const toRACDateValue = (date: string | null | undefined): CalendarDate | null | undefined => {
    if (date === null) {
      return null;
    }

    if (date === undefined) {
      return undefined;
    }

    try {
      return parseDate(date);
    } catch {
      return null;
    }
  };

  const variables = {
    limit,
    page,
    dateFrom: appliedDateFrom || undefined,
    dateTo: appliedDateTo,
    adSystem: getAdSystemForQuery(appliedSystemFilter),
    kinds: getKindsForQuery(appliedTransactionTypeFilter),
  };

  const { data, loading } = useCreditTransactionsQuery({
    variables,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const submitFilter = () => {
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setAppliedSystemFilter(systemFilter);
    setAppliedTransactionTypeFilter(transactionTypeFilter);
    setPage(1);
  };

  const clearFilter = () => {
    const today = DateTime.local().toFormat("yyyy-MM-dd");
    setDateFrom("");
    setDateTo(today);
    setSystemFilter(SystemFilter.ALL);
    setTransactionTypeFilter(TransactionTypeFilter.ALL);
    setAppliedDateFrom("");
    setAppliedDateTo(today);
    setAppliedSystemFilter(SystemFilter.ALL);
    setAppliedTransactionTypeFilter(TransactionTypeFilter.ALL);
    setPage(1);
  };

  if (loading) {
    return <SmallLoader />;
  }

  const items = data?.organization?.creditTransactions ?? [];
  const totalItems = data?.organization?.creditTransactionsCount || 0;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / limit) : 0;

  const maxDate = DateTime.local().toFormat("yyyy-MM-dd");

  return (
    <div>
      <div>
        <div style={{ display: "flex", alignItems: "center", flexDirection: "row", gap: "14px" }}>
          <div className="performance-stats-filter-item" style={{ flex: 1 }}>
            <div>{t("filter.date from")}</div>
            <ExtendedDateField
              label={t("filter.date from")}
              maxValue={toRACDateValue(maxDate) ?? undefined}
              value={dateFrom}
              labelHidden
              onChange={(value) => value && setDateFrom(value)}
            />
          </div>
          <div className="performance-stats-filter-item" style={{ flex: 1 }}>
            <div>{t("filter.date to")}</div>
            <ExtendedDateField
              label={t("filter.date to")}
              maxValue={toRACDateValue(maxDate) ?? undefined}
              minValue={toRACDateValue(dateFrom) ?? undefined}
              value={dateTo}
              labelHidden
              onChange={(value) => value && setDateTo(value)}
            />
          </div>
          {hasMetaCampaignsEnabled && (
            <div className="performance-stats-filter-item" style={{ flex: 1 }}>
              <div>{t("filter.system")}</div>
              <SingleSelectField
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                label={t("filter.system")}
                options={systemOptions}
                value={systemFilter}
                labelHidden
                onChange={(value) => value && setSystemFilter(value as SystemFilter)}
              />
            </div>
          )}
          <div className="performance-stats-filter-item" style={{ flex: 1 }}>
            <div>{t("filter.transaction type")}</div>
            <SingleSelectField
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
              label={t("filter.transaction type")}
              options={transactionTypeOptions}
              value={transactionTypeFilter}
              labelHidden
              onChange={(value) => value && setTransactionTypeFilter(value as TransactionTypeFilter)}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexDirection: "row",
            margin: "24px 0 36px",
          }}
        >
          <Button size="sm" type="button" variant="action" onClick={submitFilter}>
            {t("filter.button")}
          </Button>
          <IconButton
            icon="shp-cross"
            size="sm"
            style={{ marginLeft: "10px" }}
            type="button"
            variant="primary"
            onClick={clearFilter}
          />
        </div>
      </div>

      {items.length === 0 ? (
        <div style={{ padding: "128px 0", textAlign: "center" }}>
          <span
            style={{
              fontSize: "28px",
              color: "var(--app-text-muted)",
              fontFamily: "Inter Display', Arial, sans-serif",
            }}
          >
            {t("empty state")}
          </span>
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t("table header.date")}</TableHeaderCell>
                <TableHeaderCell>{t("table header.transaction type")}</TableHeaderCell>
                <TableHeaderCell>{t("table header.system")}</TableHeaderCell>
                <TableHeaderCell>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {t("table header.credit")}
                    <Tooltip closeDelay={300} content={t("table credit tooltip")} delay={200} placement="bottom">
                      <IconButton icon="shp-info" ghost />
                    </Tooltip>
                  </div>
                </TableHeaderCell>
              </TableRow>
            </TableHeader>

            <TableBody>
              {items.map((creditTransaction) => (
                <TableRow key={creditTransaction.id}>
                  <TableCell>{formatTime(creditTransaction.createdAt)}</TableCell>
                  <TableCell>{t(`transaction kind.${creditTransaction.kind}`)}</TableCell>
                  <TableCell>
                    {creditTransaction.adSystem === "GOOGLE_ADS"
                      ? t("Google campaigns")
                      : creditTransaction.adSystem === "META_ADS"
                      ? t("Meta campaigns")
                      : ""}
                  </TableCell>
                  <TableCell>
                    <div
                      className="text-right"
                      style={{
                        color:
                          (creditTransaction?.credit.amount || 0) < 0
                            ? "var(--app-critical)"
                            : "var(--app-brand-green)",
                      }}
                    >
                      {formatMoney(creditTransaction.credit, 2)}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Pagination
            jump={
              totalPages > 1 ? (
                <Pagination.Jump
                  label={tPagination("show page")}
                  totalPages={totalPages}
                  getLinkProps={(_page) => ({
                    onClick: () => setPage(_page),
                    href: `javascript:void(0)`,
                  })}
                />
              ) : null
            }
            limits={
              <Pagination.Limits
                itemsPerPage={limit}
                label={tPagination("items per page")}
                onChange={(value) => {
                  setLimit(value as ItemsPerPage);
                  localStorage.setItem("shoptet_credit_transactions_items_per_page", value.toString());
                  setPage(1);
                }}
              />
            }
            pages={
              <Pagination.Pages
                nextPageUrl={totalPages > 1 ? "javascript:void(next)" : null}
                pageCurrent={page}
                pagesTotal={totalPages}
                previousPageUrl={totalPages > 1 ? "javascript:void(prev)" : null}
                getLinkProps={(_page) => ({
                  onClick: () => {
                    let desiredPage = _page;
                    if (_page === "javascript:void(prev)") {
                      desiredPage = page - 1 < 1 ? 1 : page - 1;
                    }
                    if (_page === "javascript:void(next)") {
                      desiredPage = page + 1 > totalPages ? totalPages : page + 1;
                    }
                    setPage(desiredPage as number);
                  },
                  href: `javascript:void(0)`,
                })}
              />
            }
            summary={
              totalItems > 0
                ? asRichText(
                    <Trans
                      components={{ strong: <strong /> }}
                      i18nKey="displaying items total"
                      t={tPagination}
                      values={{ totalItems }}
                    />
                  )
                : undefined
            }
          />
        </>
      )}
    </div>
  );
};
